import queryString from "query-string";
import { AnalyseQueryParams } from "./types";

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const normalizeSearch = (search: string) => {
  const searchBody = search.startsWith("?") ? search.slice(1) : search;
  if (!searchBody) return "";
  // Support both formats:
  // /analyse/tab?param1=v1&param2=v2 and /analyse/tab?param1=v1@param2=v2
  return searchBody.replace(/@(?=[^@=&?]+=[^@=&]*)/g, "&");
};

export const parseAnalyseQueryParams = (search: string): AnalyseQueryParams => {
  const normalizedSearch = normalizeSearch(search);
  if (!normalizedSearch) return {};

  const parsedParams = queryString.parse(normalizedSearch);
  const cleanParams: AnalyseQueryParams = {};

  Object.keys(parsedParams).forEach((key) => {
    const rawValue = parsedParams[key];
    if (Array.isArray(rawValue)) {
      cleanParams[key] = rawValue.join(",");
      return;
    }
    if (rawValue === null || typeof rawValue === "undefined") {
      cleanParams[key] = "";
      return;
    }
    cleanParams[key] = String(rawValue);
  });

  return cleanParams;
};

export const formatParams = (params: AnalyseQueryParams) => {
  const keys = Object.keys(params).sort();
  if (keys.length === 0) return "aucun";
  return keys.map((key) => `${key}=${params[key]}`).join(" | ");
};

export const buildSeedFromParams = (params: AnalyseQueryParams, scope: string) => {
  let seed = scope
    .split("")
    .reduce((acc, char, idx) => acc + char.charCodeAt(0) * (idx + 1), 0);

  Object.keys(params)
    .sort()
    .forEach((key, keyIndex) => {
      const value = `${key}:${params[key]}`;
      for (let i = 0; i < value.length; i += 1) {
        seed += value.charCodeAt(i) * (i + 1 + keyIndex);
      }
    });

  return Math.abs(seed);
};

export const readNumericParam = (
  params: AnalyseQueryParams,
  paramNames: string[],
  fallback: number
) => {
  for (const name of paramNames) {
    const value = params[name];
    if (typeof value === "undefined") continue;
    const parsed = Number(value.replace(",", "."));
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

export const buildChartSeries = (
  labels: string[],
  params: AnalyseQueryParams,
  scope: string
) => {
  const seed = buildSeedFromParams(params, scope);
  const multiplier = clamp(
    readNumericParam(params, ["scale", "coef", "ratio"], 1),
    0.6,
    2.4
  );

  return labels.map((label, idx) => {
    const raw = 25 + ((seed + idx * 19) % 60);
    const value = clamp(Math.round(raw * multiplier), 10, 100);
    return { label, value };
  });
};

export const buildDynamicInteger = (
  params: AnalyseQueryParams,
  scope: string,
  index: number,
  min: number,
  max: number
) => {
  const seed = buildSeedFromParams(params, `${scope}-${index}`);
  return min + (seed % (max - min + 1));
};
